import { getCubicBezierLength } from '@canvas/bezier';
import { clamp } from '@canvas/math/numbers';

import { type PathProportion, type PathSegment } from '../types.ts';

import { getArcLength } from './arc.ts';
import { createCubicBezierFromSegment } from './cubic-bezier.ts';
import { getLineLength } from './line.ts';

/**
 * Returns the length of a segment of any type
 *
 * @param segment
 * @returns number
 */

export function getSegmentLength(segment: PathSegment): number {
  if (segment.type === 'line') {
    return getLineLength(segment);
  }
  if (segment.type === 'arc') {
    return getArcLength(segment);
  }
  if (segment.type === 'cubic-bezier') {
    // no need to pass in start point to compute bezier length
    const bezier = createCubicBezierFromSegment(segment);
    return getCubicBezierLength(bezier);
  }
  return 0;
}

/**
 * Returns the length of an array of segments, of any type
 *
 * @param segments
 * @returns number
 */

export function getSegmentArrayLength(segments: PathSegment[]): number {
  return segments.map((segment) => getSegmentLength(segment)).reduce((a, b) => a + b);
}

export type SegmentPathProportion = {
  /**
   * the path segment found at the given proportion value
   */
  segment: PathSegment;
  /**
   * the proportion value at the start of the segment
   */
  proportionAtStart: number;
  /**
   * the proportion value at the end of the segment
   */
  proportionAtEnd: number;
  /**
   * the how far along the segment from 0 - 1 is the point
   * that lies at the given proportion value
   */
  proportionWithinSegment: number;
  /**
   * the index of the segment within the segments array
   */
  index: number;
};

/**
 * Returns details about the segment located at a proportion of a path
 *
 * @param segments - array of segments of a path
 * @param proportion - a number from 0 to 1 of the target proportion along the path
 * values outside the range 0 - 1 will be clamped
 * @returns SegmentPathProportion data
 */
export function getSegmentAtPathProportion(
  segments: PathSegment[],
  proportion: PathProportion,
): SegmentPathProportion {
  const clampedProportion = clamp(0, 1, proportion);
  const segmentLengths = segments.map((segment) => getSegmentLength(segment));
  const totalLength = segmentLengths.reduce((a, b) => a + b);

  let lengthSoFar = 0;
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const length = segmentLengths[i];
    const proportionAtStart = lengthSoFar / totalLength;
    const proportionAtEnd = (lengthSoFar + length) / totalLength;
    const proportionRange = proportionAtEnd - proportionAtStart;

    const segmentIsInRange =
      proportionAtStart <= clampedProportion && proportionAtEnd > clampedProportion;
    const segmentIsLast = i + 1 === segments.length;

    if (segmentIsInRange || segmentIsLast) {
      const proportionWithinSegment =
        (clampedProportion - proportionAtStart) / proportionRange;
      return {
        segment,
        proportionAtStart,
        proportionAtEnd,
        proportionWithinSegment: proportionRange === 0 ? 0 : proportionWithinSegment,
        index: i,
      };
    }

    lengthSoFar += length;
  }

  // this will never be reached due to the above segmentIsLast check
  throw new Error('no segment found at path proportion');
}
